<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { api, asListResponse, type ListResponse, type Me } from '@/api'
import { emptyPaletteSources, type PaletteResult, paletteEntries, searchPalette } from '@/lib/commandPalette'

/**
 * Keyboard jump (#16): Cmd/Ctrl+K — or a bare "g" when you are not typing in a field — opens one
 * search across the enterprise, teams, members, roles, tasks, executions and webhooks. Picking a
 * result goes to that record's own detail route and closes the palette; Escape or a click outside
 * closes it and leaves the route alone. An empty query lists nothing: the palette answers a name
 * you already know, it is not a second navigation menu.
 *
 * Results are `<a>` elements with the real href, so a click, a middle click and "open in new tab"
 * all behave like every other link in the portal.
 */

/** One page of each list is plenty to jump by name; the palette is not a browser. */
const PAGE_SIZE = 100

/**
 * Nothing else in the portal says how to reach the palette, so the palette says it itself: while the
 * query is still empty it spells out the keys, in a lighter tone than the prompt above it.
 */
const SHORTCUT_HINT = 'Press ⌘K / Ctrl+K — or just G — to open this anytime'

/**
 * A button elsewhere in the shell (the header search icon) may open the palette by calling the
 * exposed `open()`. That same click then bubbles to the document listener below, which would read it
 * as a click outside the panel and close the palette again, so clicks from a marked trigger are
 * ignored while it is open.
 */
const TRIGGER_SELECTOR = '[data-command-palette-trigger]'

const router = useRouter()
const isOpen = ref(false)
const loading = ref(false)
const query = ref('')
const entries = ref<PaletteResult[]>([])
const activeIndex = ref(0)
const panel = ref<HTMLElement | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)
/** Where the keyboard was before the palette took over, so closing it hands focus straight back. */
const previouslyFocused = ref<HTMLElement | null>(null)

const results = computed(() => searchPalette(entries.value, query.value))

const emptyMessage = computed(() => {
    if (query.value.trim() === '') {
        return 'Type a name, slug or id to jump to it.'
    }
    return loading.value ? 'Searching…' : 'No matches.'
})

const showShortcutHint = computed(() => query.value.trim() === '')

watch(results, () => {
    activeIndex.value = 0
})

onMounted(() => {
    window.addEventListener('keydown', onKeydown)
    document.addEventListener('click', onDocumentClick)
})

onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeydown)
    document.removeEventListener('click', onDocumentClick)
})

/** A list endpoint the palette reads for names; an unreachable one costs its records, not the palette. */
async function safeList<T>(call: () => Promise<ListResponse<T> | T[]>): Promise<T[]> {
    try {
        return asListResponse(await call()).data
    } catch {
        return []
    }
}

async function safeMe(): Promise<Me | null> {
    try {
        return await api.me()
    } catch {
        return null
    }
}

/**
 * Every open starts a fresh load, and only the newest one may write to `entries`. Open, close and
 * reopen quickly (or sit on a slow link) and the earlier fetch can still be in flight: without this
 * token it would settle last and overwrite the fresher list with stale, possibly renamed or deleted,
 * records.
 */
let loadToken = 0

async function loadEntries(): Promise<void> {
    const token = ++loadToken
    loading.value = true
    try {
        const [me, teams, members, roles, tasks, executions, webhooks] = await Promise.all([
            safeMe(),
            safeList(() => api.teams()),
            safeList(() => api.members()),
            safeList(() => api.roles()),
            safeList(() => api.tasks({ limit: PAGE_SIZE })),
            safeList(() => api.executions({ limit: PAGE_SIZE })),
            safeList(() => api.webhooks({ limit: PAGE_SIZE })),
        ])
        if (token !== loadToken) {
            return
        }
        entries.value = paletteEntries({
            ...emptyPaletteSources(),
            enterprise: me?.enterprise ?? null,
            teams,
            members,
            roles,
            tasks,
            executions,
            webhooks,
        })
    } finally {
        if (token === loadToken) {
            loading.value = false
        }
    }
}

async function openPalette(): Promise<void> {
    if (isOpen.value) {
        return
    }
    previouslyFocused.value = document.activeElement instanceof HTMLElement ? document.activeElement : null
    isOpen.value = true
    query.value = ''
    activeIndex.value = 0
    entries.value = []
    await nextTick()
    searchInput.value?.focus()
    await loadEntries()
}

function closePalette(): void {
    isOpen.value = false
    query.value = ''
    const restoreTo = previouslyFocused.value
    previouslyFocused.value = null
    if (restoreTo && restoreTo !== document.body && restoreTo.isConnected) {
        restoreTo.focus()
    }
}

/** Everything inside the panel that can take focus, in DOM order: the search box, then the results. */
function panelFocusables(): HTMLElement[] {
    const root = panel.value
    if (!root) {
        return []
    }
    return Array.from(root.querySelectorAll<HTMLElement>('input, a[href], button'))
}

/**
 * `role="dialog" aria-modal="true"` promises assistive tech that Tab stays inside the palette while
 * it is open, so cycle focus through the panel ourselves instead of letting it land on the page
 * behind the backdrop.
 */
function trapTab(event: KeyboardEvent): void {
    const focusables = panelFocusables()
    if (focusables.length === 0) {
        return
    }
    event.preventDefault()
    const step = event.shiftKey ? -1 : 1
    focusables[nextFocusIndex(focusables, step)]?.focus()
}

/** Where Tab (step 1) or Shift+Tab (step -1) lands next, wrapping, and entering at either end. */
function nextFocusIndex(focusables: HTMLElement[], step: number): number {
    const active = document.activeElement
    const current = active instanceof HTMLElement ? focusables.indexOf(active) : -1
    if (current === -1) {
        return step === 1 ? 0 : focusables.length - 1
    }
    return (current + step + focusables.length) % focusables.length
}

function moveActive(step: number): void {
    const count = results.value.length
    if (count === 0) {
        return
    }
    activeIndex.value = (activeIndex.value + step + count) % count
}

async function selectActive(): Promise<void> {
    const result = results.value[activeIndex.value]
    if (!result) {
        return
    }
    closePalette()
    await router.push(result.to)
}

/** True while the keystroke belongs to whatever the CEO is typing into, not to the palette. */
function isTypingInAField(): boolean {
    const active = document.activeElement
    if (!(active instanceof HTMLElement)) {
        return false
    }
    const tag = active.tagName.toLowerCase()
    return tag === 'input' || tag === 'textarea' || tag === 'select' || active.isContentEditable
}

function isToggleShortcut(event: KeyboardEvent): boolean {
    return (event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k'
}

function isJumpShortcut(event: KeyboardEvent): boolean {
    return event.key === 'g' && !event.metaKey && !event.ctrlKey && !event.altKey && !isTypingInAField()
}

const ARROW_STEPS: Record<string, number> = { ArrowDown: 1, ArrowUp: -1 }

function handleOpenKey(event: KeyboardEvent): void {
    const step = ARROW_STEPS[event.key] ?? 0
    if (step !== 0) {
        event.preventDefault()
        moveActive(step)
        return
    }
    if (event.key === 'Tab') {
        trapTab(event)
        return
    }
    if (event.key === 'Escape') {
        event.preventDefault()
        closePalette()
        return
    }
    if (event.key === 'Enter') {
        event.preventDefault()
        void selectActive()
    }
}

function onKeydown(event: KeyboardEvent): void {
    if (isToggleShortcut(event)) {
        event.preventDefault()
        if (isOpen.value) {
            closePalette()
        } else {
            void openPalette()
        }
        return
    }
    if (isOpen.value) {
        handleOpenKey(event)
        return
    }
    if (isJumpShortcut(event)) {
        event.preventDefault()
        void openPalette()
    }
}

/** The header search button opens the palette without knowing anything about its internals. */
defineExpose({ open: openPalette })

function onDocumentClick(event: MouseEvent): void {
    if (!isOpen.value) {
        return
    }
    const target = event.target
    if (target instanceof Node && panel.value?.contains(target)) {
        return
    }
    if (target instanceof Element && target.closest(TRIGGER_SELECTOR)) {
        return
    }
    closePalette()
}
</script>

<template>
	<div
		v-if="isOpen"
		class="bg-background/70 fixed inset-0 z-50 flex items-start justify-center p-4 pt-[12vh] backdrop-blur-sm"
	>
		<div
			ref="panel"
			role="dialog"
			aria-modal="true"
			aria-label="Jump to"
			class="bg-popover text-popover-foreground w-full max-w-xl overflow-hidden rounded-xl border shadow-lg"
		>
			<input
				ref="searchInput"
				v-model="query"
				type="text"
				aria-label="Jump to a record"
				placeholder="Jump to an enterprise, team, member, role, task, execution or webhook…"
				class="w-full border-b bg-transparent px-4 py-3 text-sm outline-none"
			/>
			<ul v-if="results.length > 0" class="max-h-80 overflow-y-auto py-1">
				<li v-for="(result, index) in results" :key="result.key">
					<RouterLink
						:to="result.to"
						class="hover:bg-accent hover:text-accent-foreground flex items-center gap-3 px-4 py-2 text-sm"
						:class="index === activeIndex ? 'bg-accent text-accent-foreground' : ''"
						@click="closePalette()"
					>
						<span class="text-muted-foreground w-24 shrink-0 text-xs tracking-wide uppercase">
							{{ result.label }}
						</span>
						<span class="truncate font-medium">{{ result.title }}</span>
						<span v-if="result.subtitle" class="text-muted-foreground ml-auto truncate text-xs">
							{{ result.subtitle }}
						</span>
					</RouterLink>
				</li>
			</ul>
			<p v-else class="text-muted-foreground px-4 py-3 text-sm">{{ emptyMessage }}</p>
			<p v-if="showShortcutHint" class="text-muted-foreground/60 border-t px-4 py-2 text-xs">
				{{ SHORTCUT_HINT }}
			</p>
		</div>
	</div>
</template>
